import {
  Fragment,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  AccountLastNoteCell,
  AccountLastNoteDateCell,
} from '@/components/companies/AccountLastNoteCell';
import {
  ACCOUNT_LAST_NOTE_AT_LABEL,
  ACCOUNT_LAST_NOTE_LABEL,
  accountLastNoteDate,
  accountLastNoteFilterText,
} from '@/components/companies/account-last-note';
import { OpenNotesButton } from '@/components/companies/OpenNotesButton';
import { DealNameLink } from '@/components/deals/DealNameLink';
import { ActivityDrawer } from '@/components/tasks/ActivityDrawer';
import { PlusIcon } from '@/components/catalog/icons';
import {
  Avatar,
  Badge,
  Button,
  FilterableTH,
  FilterSheet,
  Panel,
  PanelBody,
  PanelHeader,
  Select,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
  ValuePills,
} from '@/components/ui';
import type { SortDirection } from '@/components/ui';
import { Money } from '@/components/ui/Money';
import { TextLink } from '@/components/ui/TextLink';
import { ApiError } from '@/lib/api';
import { QUOTE_SIGNED_FILTER_OPTIONS, QUOTE_SIGNED_LABEL, quoteSignedLabel } from '@/lib/deal-quote-signed';
import { splitByCompanyGroup } from '@/lib/company-group';
import { formatDateOnly, pluralise } from '@/lib/format';
import { isOverCapacity, laneCapacityFigures, laneTargetLabel } from '@/lib/sales-tracker';
import type {
  SalesTrackerDealBoard,
  SalesTrackerDealLane,
  SalesTrackerDealLaneProject,
  SalesTrackerDealRow,
  SalesTrackerDealStage,
} from '@/lib/types';
import { viewerZone } from '@/lib/task-time';
import { useAuth } from '@/lib/use-auth';
import { useFrozenOffsets, type ColumnLayoutController } from '@/lib/use-column-layout';
import { useTableFilters, type ColumnSpec } from '@/lib/use-table-filters';

/**
 * The sales tracker as a TABLE — one row per DEAL × PROJECT, banded by project.
 *
 * ## 🔴 THE ROWS ARE DEALS — owner's decision 2026-10-12
 *
 * *SUPERSEDED: one row per PURSUIT, with editable status, priority, units and
 * cities, an Edit pencil, a Delete control and a create/combine menu per row.*
 * The pursuit concept is dropped. A deal shows in EVERY band its line items name
 * (so the same deal can appear more than once on this table, legitimately), and
 * a deal whose lines name no project sits in the trailing "No project" band.
 *
 *  - **The deal's STAGE is editable in place** — `PATCH /deals/{id}` with
 *    `{ stageId }` and nothing else — for a reader who may update deals.
 *  - **Units and cities are READ-ONLY** — they are derived from the deal's lines.
 *  - **Priority is gone.**
 *  - **There is no actions column.** The deal NAME opens the deal in a modal
 *    (`DealNameLink`, behind the page's `DealQuickEdit`), and a modified click
 *    still opens `/deals/{id}` in a new tab — so a separate ↗ would be a second
 *    route to the same place.
 *
 * ## The DEAL column is the frozen one now
 *
 * *SUPERSEDED: Company code-pinned left (2026-10-11).* The deal is what
 * identifies a row — one company can hold several deals in one band — so the
 * Deal column is the row header, always first and frozen to the left edge.
 * Company is an ordinary column a reader may freeze beside it.
 *
 * ## 🔴 FILTERING AND SORTING ARE CLIENT-SIDE, AND THAT IS CORRECT HERE
 *
 * `GET /sales-tracker/board` returns EVERY band and EVERY row for the cycle in
 * one response, with no paging — so a page-scoped filter narrows the complete
 * set and a client sort orders all of it. Sorting reorders rows INSIDE each band
 * and never the bands, whose order is the manual project order (null band last).
 */

/* ------------------------------------------------------------------ *
 * The column set
 * ------------------------------------------------------------------ */

/** The column that IDENTIFIES the row — a `<th scope="row">`, pinned left. */
const ROW_HEADER_KEY = 'deal';

/**
 * Every column, in DECLARED order. Module-level and frozen, because
 * `useColumnLayout` holds it and keys its memo off `JSON.stringify(keys)`.
 *
 * ⚠️ **This list changed wholesale on 2026-10-12**, so every reader's stored
 * column arrangement resets once, silently — the hook drops keys it no longer
 * knows. Accepted, and the same cost this list has paid before.
 */
export const SALES_TRACKER_COLUMN_KEYS: readonly string[] = Object.freeze([
  'deal',
  'company',
  'type',
  'stage',
  'quoteSigned',
  'units',
  'cities',
  'value',
  'closingDate',
  'accountLead',
  'lastNote',
  'lastNoteAt',
]);

/** The Deal column may never be hidden or moved — it identifies every row. */
export const SALES_TRACKER_LOCKED_KEYS: readonly string[] = Object.freeze([ROW_HEADER_KEY]);

/**
 * Deal is always the first column and always frozen to the left edge. ⛔ Never
 * in `useFrozenOffsets`' `pinnedKeys`, which means the RIGHT edge.
 */
export const SALES_TRACKER_LAYOUT_OPTIONS = Object.freeze({
  pinnedLeftKeys: Object.freeze([ROW_HEADER_KEY]),
});

/** For `ColumnManager` only: the pinned column draws no freeze buttons. */
export const SALES_TRACKER_MANAGER_PINNED_KEYS: readonly string[] = Object.freeze([ROW_HEADER_KEY]);

/**
 * Columns rendered with `pinned` (`sticky right-0`). ⚠️ **Empty since the
 * actions column went** — kept as a constant because the hook and
 * `useFrozenOffsets` both take it, and a fresh literal per render would rebuild
 * the measuring effect's `ResizeObserver` every paint.
 */
export const SALES_TRACKER_PINNED_KEYS: readonly string[] = Object.freeze([]);

/** Why a locked column is locked, in the reader's own terms. */
export function salesTrackerLockReason(key: string): string | undefined {
  if (key === ROW_HEADER_KEY) return 'Always first and frozen, so every row shows its deal';
  return undefined;
}

/**
 * The width each column is sized for, in px. The table's min-width is the SUM
 * of the visible ones, applied through `style` — ⛔ a computed
 * `min-w-[${n}px]` emits no rule in Tailwind v4.
 */
const COLUMN_WIDTHS: Record<string, number> = {
  deal: 260,
  company: 220,
  type: 140,
  stage: 190,
  quoteSigned: 160,
  units: 90,
  cities: 200,
  value: 150,
  closingDate: 130,
  accountLead: 180,
  lastNote: 280,
  lastNoteAt: 170,
};

/** The header a column shows — and the same string the column manager lists. */
export function labelOfSalesTrackerColumn(key: string): string {
  switch (key) {
    case 'deal':
      return 'Deal';
    case 'company':
      return 'Company';
    case 'type':
      return 'Type';
    case 'stage':
      return 'Deal status';
    // ⛔ Never fork the wording — it lives in `deal-quote-signed.ts`.
    case 'quoteSigned':
      return QUOTE_SIGNED_LABEL;
    case 'units':
      return 'Units';
    case 'cities':
      return 'Cities';
    case 'value':
      return 'Value';
    case 'closingDate':
      return 'Closing date';
    case 'accountLead':
      return 'Account lead';
    // ⛔ Never fork the wording — both strings live in `account-last-note.ts`.
    case 'lastNote':
      return ACCOUNT_LAST_NOTE_LABEL;
    case 'lastNoteAt':
      return ACCOUNT_LAST_NOTE_AT_LABEL;
    default:
      return key;
  }
}

/* ------------------------------------------------------------------ *
 * Props
 * ------------------------------------------------------------------ */

export interface SalesTrackerTableProps {
  board: SalesTrackerDealBoard;
  /** The column layout, owned by the page so its `ColumnManager` agrees. */
  layout: ColumnLayoutController;
  /**
   * Change one deal's stage. Omit and the stage is read-only.
   *
   * 🔴 **IT MUST REJECT ON FAILURE AND RESOLVE ONLY ON A LANDED WRITE** — the
   * cell renders the refusal beside the control, and a swallowed error would
   * leave a stage on screen the server never stored.
   */
  onUpdateStage?: (row: SalesTrackerDealRow, stageId: number) => Promise<void>;
  /**
   * Called once when the notes drawer CLOSES after a note or task was written
   * in it, so the board re-reads each company's last note. ⚠️ It must not blank
   * or dim the table.
   */
  onNotesChanged?: () => void;
  /**
   * The band "+" — owner 2026-10-23: open the New deal form with this project's
   * line items already added. Omit and no band carries the button. ⚠️ Shown
   * only on a band WITH a project, and re-gated on `canCreate('deals')` here.
   */
  onNewDealForProject?: (project: SalesTrackerDealLaneProject) => void;
  /** The project whose "+" is still reading the project, so its button spins. */
  newDealPendingProjectId?: number | null;
}

/** The band "+": icon-only, its name as the hover label. */
function BandNewDealButton({
  project,
  onClick,
  pending,
}: {
  project: SalesTrackerDealLaneProject;
  onClick: (project: SalesTrackerDealLaneProject) => void;
  pending: boolean;
}) {
  const label = `New deal for ${bandLabel(project)}`;
  return (
    <Button
      variant="ghost"
      iconOnly
      leadingIcon={<PlusIcon />}
      aria-label={label}
      tooltip={label}
      loading={pending}
      className="shrink-0"
      onClick={() => onClick(project)}
    />
  );
}

/** One row: a deal, and the band it came out of. */
interface DealRowEntry {
  deal: SalesTrackerDealRow;
  /** The lane's index in `board.lanes` — a band's only identity, since `project` can be `null`. */
  laneIndex: number;
  lane: SalesTrackerDealLane;
}

/** A band: a project (or none), its figures, and the rows under it. */
interface DealBand {
  key: string;
  lane: SalesTrackerDealLane;
  rows: DealRowEntry[];
}

/**
 * The company group (Institutions, Partners) a row lists under — by the type
 * master's `sortOrder`, then id; no group first. 🔴 Owner 2026-10-18: every
 * sort compares this FIRST and it is PINNED ascending, never flipped by the
 * sort direction, so a group never splinters across a band. ⛔ Never by name.
 */
function compareGroup(a: DealRowEntry, b: DealRowEntry): number {
  const left = a.deal.companyGroup ?? null;
  const right = b.deal.companyGroup ?? null;
  if (left === null || right === null) {
    if (left === right) return 0;
    return left === null ? -1 : 1;
  }
  if (left.sortOrder !== right.sortOrder) return left.sortOrder - right.sortOrder;
  return left.id - right.id;
}

/** Group, then company A–Z, then deal name — the default arrangement inside a band. */
function compareDefault(a: DealRowEntry, b: DealRowEntry): number {
  const byGroup = compareGroup(a, b);
  if (byGroup !== 0) return byGroup;
  const options = { numeric: true, sensitivity: 'base' } as const;
  const companyA = a.deal.company?.name.trim() ?? '';
  const companyB = b.deal.company?.name.trim() ?? '';
  const byCompany = companyA.localeCompare(companyB, undefined, options);
  if (byCompany !== 0) return byCompany;
  return a.deal.dealName.trim().localeCompare(b.deal.dealName.trim(), undefined, options);
}

/**
 * One band per LANE, empty lanes included — used whenever none of the table's
 * own column filters is narrowing anything (owner's decision 2026-10-04: every
 * project in the cycle shows as a band).
 */
function bandByLane(lanes: SalesTrackerDealLane[]): DealBand[] {
  return lanes.map((lane, laneIndex) => ({
    key: `lane:${laneIndex}:${lane.project?.id ?? 'none'}`,
    lane,
    rows: lane.deals.map((deal) => ({ deal, laneIndex, lane })).sort(compareDefault),
  }));
}

/**
 * Groups CONSECUTIVE runs of filtered rows by lane, then sorts INSIDE each run.
 * ⛔ Runs, never a map — rows arrive in lane order, and sorting across runs would
 * shatter every band.
 */
function bandByRuns(rows: DealRowEntry[]): DealBand[] {
  const bands: DealBand[] = [];
  for (const row of rows) {
    const key = `lane:${row.laneIndex}:${row.lane.project?.id ?? 'none'}`;
    const last = bands.at(-1);
    if (last && last.key === key) last.rows.push(row);
    else bands.push({ key, lane: row.lane, rows: [row] });
  }
  for (const band of bands) band.rows.sort(compareDefault);
  return bands;
}

/** What a band is called — a blank project name is named by id, never "no project". */
function bandLabel(project: SalesTrackerDealLaneProject | null): string {
  if (project === null) return 'No project';
  return project.name.trim() || `Project #${project.id}`;
}

/**
 * A band's rows as consecutive company-group runs (sub-header per run, even a
 * lone one). Ungrouped = one run with no heading. Never re-sorts.
 */
function runsOf(
  band: DealBand,
  grouped: boolean,
): { key: string; label: string | null; rows: DealRowEntry[] }[] {
  if (!grouped) return [{ key: 'all', label: null, rows: band.rows }];
  return splitByCompanyGroup(band.rows, (row) => row.deal.companyGroup);
}

/** A group sub-header: "Institutions · 24". */
function GroupHeading({ label, count }: { label: string; count: number }) {
  return (
    <>
      {label}
      <span aria-hidden="true"> &middot; </span>
      <span className="sr-only">, </span>
      <span className="tabular">{count}</span>
    </>
  );
}

/** The one sentence an empty band carries, in both layouts. */
const EMPTY_BAND_SENTENCE = 'No deals on this project yet.';

/**
 * What a band heading says, in both layouts.
 *
 * Owner 2026-10-27: per category, sold against planned capacity and target —
 * "Institutions 12 of 30 · target 20 · Partners 3 of 5 · target 4 · Quoted 7" —
 * each category followed by its state badge (Target met / Over capacity; over
 * capacity is allowed, so neutral), then **N deals** (`counts.deals`). Wording and
 * state rules live in `lib/project-capacity.ts` via `laneCapacityFigures`.
 * *SUPERSEDED (kept as the fallback when the server sends no `capacityPlan` /
 * `counts.sold`): "Booked N of C" from `counts.accepted`, plus "Over target —
 * allowed".* ⚠️ **Every figure is project-level and does not narrow with the
 * table's filters.** The "No project" band carries only the deals count.
 */
function BandHeading({ band }: { band: DealBand }) {
  const { project, counts } = band.lane;
  const figures = project === null ? null : laneCapacityFigures(project, counts);
  const over =
    project !== null && figures === null && isOverCapacity(counts.accepted, project.capacity);
  return (
    <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
      <span className="min-w-0 truncate">{bandLabel(project)}</span>
      {project !== null && !project.isActive && (
        <Badge tone="warning" dot>
          Retired project
        </Badge>
      )}
      <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1 text-2xs font-normal">
        {figures !== null && (
          <>
            {figures.categories.map((category, index) => (
              <Fragment key={category.key}>
                {index > 0 && <span aria-hidden="true" className="text-fg-muted">&middot;</span>}
                <span className="font-semibold text-fg-secondary tabular">{category.line}</span>
                {category.badge !== null && (
                  <Badge tone={category.badge.tone} variant="outline">
                    {category.badge.label}
                  </Badge>
                )}
              </Fragment>
            ))}
            {figures.quoted !== null && (
              <>
                <span aria-hidden="true" className="text-fg-muted">&middot;</span>
                <span>
                  <span className="text-fg-muted">Quoted</span>{' '}
                  <span className="font-semibold text-fg-secondary tabular">{figures.quoted}</span>
                </span>
              </>
            )}
            <span aria-hidden="true" className="h-3 w-px shrink-0 self-center bg-border" />
          </>
        )}
        {project !== null && figures === null && (
          <>
            <span>
              <span className="text-fg-muted">Booked</span>{' '}
              <span className="font-semibold text-fg-secondary tabular">
                {laneTargetLabel(counts.accepted, project.capacity)}
              </span>
            </span>
            <span aria-hidden="true" className="h-3 w-px shrink-0 self-center bg-border" />
          </>
        )}
        <span className="text-fg-muted tabular">{pluralise(counts.deals, 'deal')}</span>
      </span>
      {over && (
        <Badge tone="neutral" variant="outline">
          Over target &mdash; allowed
        </Badge>
      )}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Sorting
 * ------------------------------------------------------------------ */

const SORT_KEYS = {
  deal: 'deal',
  company: 'company',
  type: 'type',
  stage: 'stage',
  quoteSigned: 'quoteSigned',
  units: 'units',
  cities: 'cities',
  closingDate: 'closingDate',
  accountLead: 'accountLead',
  lastNote: 'lastNote',
  lastNoteAt: 'lastNoteAt',
} as const;
type TableSortKey = (typeof SORT_KEYS)[keyof typeof SORT_KEYS];
const TABLE_SORT_KEYS: ReadonlySet<string> = new Set(Object.values(SORT_KEYS));

/** The account lead as one string, for the cell AND the filter. `''` for none. */
function accountLeadLabel(deal: SalesTrackerDealRow): string {
  const lead = deal.accountLead;
  if (lead === null) return '';
  return lead.name.trim() || lead.email.trim() || `User #${lead.id}`;
}

/** The company's RECORDED type, or `null` for none (a grouped row may still have none). */
function companyTypeName(deal: SalesTrackerDealRow): string | null {
  const name = deal.company?.type?.name.trim() ?? '';
  return name === '' ? null : name;
}

/** The account's last-note createdAt as a LOCAL day (`YYYY-MM-DD`), or `null`. */
function lastNoteDay(deal: SalesTrackerDealRow): string | null {
  const iso = accountLastNoteDate(deal.company?.accountLastNote);
  if (iso === null) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function lastNoteInstant(deal: SalesTrackerDealRow): number | null {
  const iso = accountLastNoteDate(deal.company?.accountLastNote);
  if (iso === null) return null;
  const time = new Date(iso).getTime();
  return Number.isNaN(time) ? null : time;
}

/**
 * The value a row is ordered by. ⚠️ Stages order by the master's `sortOrder`,
 * never A–Z. `units: 0` is a value and `null` is not. A closing date compares as
 * its `YYYY-MM-DD` string, never through a `Date`.
 */
function sortValueOf(
  deal: SalesTrackerDealRow,
  key: TableSortKey,
  stageOrder: ReadonlyMap<number, number>,
): number | string | null {
  switch (key) {
    case 'deal':
      return deal.dealName;
    case 'company':
      return deal.company?.name ?? null;
    case 'type':
      return companyTypeName(deal);
    case 'stage':
      return deal.stage === null ? null : (stageOrder.get(deal.stage.id) ?? null);
    case 'quoteSigned': {
      // By the signed instant; signed-but-undated sorts before every dated one,
      // and unsigned (or an older API) is the `null` end.
      const signed = deal.quoteSigned;
      if (signed === undefined || signed === null) return null;
      return signed.signedAt ?? '';
    }
    case 'units':
      return deal.units;
    case 'cities':
      return deal.cities.length;
    case 'closingDate':
      return deal.closingDate;
    case 'accountLead': {
      const label = accountLeadLabel(deal);
      return label === '' ? null : label;
    }
    case 'lastNote':
    case 'lastNoteAt':
      return lastNoteInstant(deal);
  }
}

/**
 * Rows inside each band ordered by the chosen column, IN PLACE — always INSIDE
 * the company group, which is compared first and never flips with direction.
 * `null` sorts FIRST ascending and LAST descending (MariaDB's order); ties
 * break on the deal id. ⛔ The bands themselves are never reordered.
 */
function sortBands(
  bands: DealBand[],
  key: TableSortKey,
  direction: SortDirection,
  stageOrder: ReadonlyMap<number, number>,
): void {
  const sign = direction === 'asc' ? 1 : -1;
  for (const band of bands) {
    band.rows.sort((a, b) => {
      const byGroup = compareGroup(a, b);
      if (byGroup !== 0) return byGroup;
      const left = sortValueOf(a.deal, key, stageOrder);
      const right = sortValueOf(b.deal, key, stageOrder);
      let order = 0;
      if (left === null && right !== null) order = -sign;
      else if (right === null && left !== null) order = sign;
      else if (left !== null && right !== null) {
        order =
          typeof left === 'string' && typeof right === 'string'
            ? left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' }) * sign
            : (Number(left) - Number(right)) * sign;
      }
      return order !== 0 ? order : a.deal.dealId - b.deal.dealId;
    });
  }
}

/* ------------------------------------------------------------------ *
 * The table
 * ------------------------------------------------------------------ */

export function SalesTrackerTable({
  board,
  layout,
  onUpdateStage,
  onNotesChanged,
  onNewDealForProject,
  newDealPendingProjectId = null,
}: SalesTrackerTableProps): ReactElement {
  const headRowRef = useRef<HTMLTableRowElement>(null);
  const { user, ability } = useAuth();

  /** The band "+", or nothing — no handler, no create grant, or no project. */
  const newDealButtonFor = (band: DealBand): ReactNode => {
    const project = band.lane.project;
    if (onNewDealForProject === undefined || project === null) return null;
    if (!ability.canCreate('deals')) return null;
    return (
      <BandNewDealButton
        project={project}
        onClick={onNewDealForProject}
        pending={newDealPendingProjectId === project.id}
      />
    );
  };

  const visibleKeys = layout.visibleKeys;
  const isVisible = useCallback((key: string) => visibleKeys.includes(key), [visibleKeys]);

  const offsets = useFrozenOffsets(
    headRowRef,
    visibleKeys,
    layout.frozenSideOf,
    SALES_TRACKER_PINNED_KEYS,
  );

  /*
   * The header row's rendered height — where a band heading sticks, just under
   * the frozen column headers (owner's decision 2026-10-11). Measured, because a
   * heading can wrap.
   */
  const [headHeight, setHeadHeight] = useState(0);
  useLayoutEffect(() => {
    const row = headRowRef.current;
    if (!row) return;
    const measure = () => setHeadHeight(row.getBoundingClientRect().height);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [board.lanes.length]);

  const minWidth = useMemo(
    () => visibleKeys.reduce((sum, key) => sum + (COLUMN_WIDTHS[key] ?? 0), 0),
    [visibleKeys],
  );

  /** Every row, in the order the server sent it: bands in order, deals in theirs. */
  const rows = useMemo<DealRowEntry[]>(
    () =>
      board.lanes.flatMap((lane, laneIndex) =>
        lane.deals.map((deal) => ({ deal, laneIndex, lane })),
      ),
    [board.lanes],
  );

  /** `undefined` on every row = an older API: render ungrouped, as before. */
  const grouped = useMemo(
    () => rows.some((row) => row.deal.companyGroup !== undefined),
    [rows],
  );

  /*
   * Which company's tasks-and-notes drawer is open. ⛔ ONE mount for the table,
   * never one per row — every layout opens the same drawer.
   */
  const [notesFor, setNotesFor] = useState<{ id: number; name: string } | null>(null);
  const notesChangedRef = useRef(false);
  const onOpenNotes = useCallback((company: { id: number; name: string }) => {
    notesChangedRef.current = false;
    setNotesFor({ id: company.id, name: company.name });
  }, []);
  const closeNotes = useCallback(() => {
    setNotesFor(null);
    if (notesChangedRef.current) {
      notesChangedRef.current = false;
      onNotesChanged?.();
    }
  }, [onNotesChanged]);

  const stageIndex = useMemo(
    () => new Map(board.stages.map((stage) => [stage.id, stage])),
    [board.stages],
  );
  const stageOrder = useMemo(
    () => new Map(board.stages.map((stage) => [stage.id, stage.sortOrder])),
    [board.stages],
  );

  /**
   * The filter vocabulary. Every column is `'page'`-scoped, which here is the
   * whole population; none carries `param`, `scope: 'server'` or `multi`.
   * ⚠️ `value` has no spec, so it renders a plain header: one figure across
   * mixed currencies has no honest order and no useful option list.
   */
  const filterColumns = useMemo<ColumnSpec<DealRowEntry>[]>(() => {
    // From the MASTER, never the loaded rows; a retired stage is labelled, not dropped.
    const stageOptions = board.stages.map((stage) => ({
      value: String(stage.id),
      label: stage.isActive ? stage.name : `${stage.name} (retired)`,
    }));

    // Every city any row carries, de-duplicated by ID, sorted by label.
    const seenCities = new Map<number, string>();
    for (const row of rows) {
      for (const city of row.deal.cities) {
        if (!seenCities.has(city.id)) seenCities.set(city.id, city.name.trim() || `#${city.id}`);
      }
    }
    const cityFilterOptions = [...seenCities.entries()]
      .map(([id, label]) => ({ value: String(id), label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

    return [
      {
        key: 'deal',
        label: labelOfSalesTrackerColumn('deal'),
        get: (row) => row.deal.dealName,
        sortKey: SORT_KEYS.deal,
      },
      {
        key: 'company',
        label: labelOfSalesTrackerColumn('company'),
        get: (row) => row.deal.company?.name ?? null,
        sortKey: SORT_KEYS.company,
      },
      {
        key: 'type',
        label: labelOfSalesTrackerColumn('type'),
        get: (row) => companyTypeName(row.deal),
        sortKey: SORT_KEYS.type,
      },
      {
        key: 'stage',
        label: labelOfSalesTrackerColumn('stage'),
        get: (row) => (row.deal.stage === null ? null : String(row.deal.stage.id)),
        options: stageOptions,
        sortKey: SORT_KEYS.stage,
      },
      {
        key: 'quoteSigned',
        label: labelOfSalesTrackerColumn('quoteSigned'),
        // `undefined` = an older API: empty, never "Not signed".
        get: (row) => {
          const signed = row.deal.quoteSigned;
          if (signed === undefined) return null;
          return signed === null ? 'unsigned' : 'signed';
        },
        options: QUOTE_SIGNED_FILTER_OPTIONS.map((option) => ({ ...option })),
        sortKey: SORT_KEYS.quoteSigned,
      },
      {
        key: 'units',
        label: labelOfSalesTrackerColumn('units'),
        numeric: true,
        // `null` (no counted line) is EMPTY; `0` stays the string "0".
        get: (row) => row.deal.units,
        sortKey: SORT_KEYS.units,
      },
      {
        key: 'cities',
        label: labelOfSalesTrackerColumn('cities'),
        // A SET: `get` exists only for the emptiness test, `matches` does the work.
        get: (row) => row.deal.cities.map((city) => city.id).join(','),
        matches: (row, selected) => {
          const held = new Set(row.deal.cities.map((city) => String(city.id)));
          // ANY, never ALL: ticking a second city must never REMOVE a row.
          return selected.some((value) => held.has(value));
        },
        options: cityFilterOptions,
        sortKey: SORT_KEYS.cities,
      },
      {
        key: 'closingDate',
        label: labelOfSalesTrackerColumn('closingDate'),
        // ⚠️ No `format`: the hook sorts options by LABEL and only `YYYY-MM-DD`
        // sorts chronologically.
        get: (row) => row.deal.closingDate,
        sortKey: SORT_KEYS.closingDate,
      },
      {
        key: 'accountLead',
        label: labelOfSalesTrackerColumn('accountLead'),
        get: (row) => accountLeadLabel(row.deal),
        sortKey: SORT_KEYS.accountLead,
      },
      {
        key: 'lastNote',
        label: labelOfSalesTrackerColumn('lastNote'),
        get: (row) => accountLastNoteFilterText(row.deal.company?.accountLastNote),
        sortKey: SORT_KEYS.lastNote,
      },
      {
        key: 'lastNoteAt',
        label: labelOfSalesTrackerColumn('lastNoteAt'),
        get: (row) => lastNoteDay(row.deal),
        sortKey: SORT_KEYS.lastNoteAt,
      },
    ];
  }, [board.stages, rows]);

  const filters = useTableFilters(rows, filterColumns);

  // Filter, THEN band, THEN sort inside each band.
  const filtering = filters.activeCount > 0;
  const activeSort =
    filters.sort !== null && TABLE_SORT_KEYS.has(filters.sort.key)
      ? { key: filters.sort.key as TableSortKey, direction: filters.sort.direction }
      : null;
  const sortKey = activeSort?.key ?? null;
  const sortDirection = activeSort?.direction ?? null;
  const bands = useMemo(() => {
    const built = filtering ? bandByRuns(filters.visibleRows) : bandByLane(board.lanes);
    if (sortKey !== null && sortDirection !== null) {
      sortBands(built, sortKey, sortDirection, stageOrder);
    }
    return built;
  }, [filtering, filters.visibleRows, board.lanes, sortKey, sortDirection, stageOrder]);

  /*
   * The deal currently being re-staged. A deal can sit in several bands, so the
   * pending mark is keyed on the DEAL, not the row — every copy of it disables.
   */
  const [pendingDealId, setPendingDealId] = useState<number | null>(null);
  const updateStage = useMemo(() => {
    if (onUpdateStage === undefined) return undefined;
    return async (row: SalesTrackerDealRow, stageId: number) => {
      setPendingDealId(row.dealId);
      try {
        await onUpdateStage(row, stageId);
      } finally {
        setPendingDealId(null);
      }
    };
  }, [onUpdateStage]);

  const shared: CellSharedProps = {
    stages: board.stages,
    stageIndex,
    pendingDealId,
    onUpdateStage: updateStage,
    onOpenNotes,
  };

  return (
    <>
      {/* ── The table, from `md` up ─────────────────────────────────────── */}
      <Panel className="hidden min-w-0 overflow-hidden md:block" aria-label="Deals by project">
        <PanelBody density="flush">
          {board.lanes.length === 0 ? (
            <p className="m-0 px-4 py-8 text-center text-xs text-fg-subtle">
              No deals on any project in this cycle yet.
            </p>
          ) : (
            <TableWrap frozenHeader frozenReservePx={24}>
              <Table style={{ minWidth }}>
                <THead>
                  <TR ref={headRowRef}>
                    {visibleKeys.map((key) => {
                      const side = layout.frozenSideOf(key);
                      const frozenOffset = side === undefined ? undefined : (offsets[key] ?? 0);
                      if (key === 'value') {
                        return (
                          <TH
                            key={key}
                            scope="col"
                            numeric
                            frozen={side}
                            frozenOffset={frozenOffset}
                          >
                            {labelOfSalesTrackerColumn(key)}
                          </TH>
                        );
                      }
                      return (
                        <FilterableTH
                          key={key}
                          {...filters.headerFor(key)}
                          frozen={side}
                          frozenOffset={frozenOffset}
                        />
                      );
                    })}
                  </TR>
                </THead>
                {/*
                  * The header STAYS when the filters empty the table: these funnels
                  * are the only filter controls at this width.
                  */}
                {bands.length === 0 ? (
                  <TBody>
                    <TR>
                      <TD colSpan={visibleKeys.length} className="text-center text-fg-subtle">
                        No deals match the filters on this table.
                      </TD>
                    </TR>
                  </TBody>
                ) : (
                  bands.map((band) => (
                    <TBody key={band.key}>
                      <TR>
                        {/*
                          * Sticks just under the column headers while its band
                          * scrolls past (owner, 2026-10-11). `z-[12]` sits above
                          * the frozen body cells (10) and below the headers.
                          */}
                        <TH
                          scope="row"
                          colSpan={visibleKeys.length}
                          style={{ top: headHeight }}
                          className="sticky z-[12] bg-surface-sunken font-semibold text-fg shadow-[inset_0_-1px_0_0_var(--hairline)]"
                        >
                          <span className="sticky left-3 inline-flex items-center gap-2">
                            <BandHeading band={band} />
                            {newDealButtonFor(band)}
                          </span>
                        </TH>
                      </TR>
                      {band.rows.length === 0 && (
                        <TR>
                          <TD colSpan={visibleKeys.length} className="text-fg-subtle">
                            {EMPTY_BAND_SENTENCE}
                          </TD>
                        </TR>
                      )}
                      {runsOf(band, grouped).map((run) => (
                        <Fragment key={run.key}>
                          {run.label !== null && (
                            <TR>
                              <TD
                                colSpan={visibleKeys.length}
                                className="bg-surface py-1.5 text-2xs font-medium text-fg-muted"
                              >
                                <span className="sticky left-3 inline-block">
                                  <GroupHeading label={run.label} count={run.rows.length} />
                                </span>
                              </TD>
                            </TR>
                          )}
                          {run.rows.map((row) => (
                            <RowCells
                              key={row.deal.key}
                              deal={row.deal}
                              visibleKeys={visibleKeys}
                              frozenSideOf={layout.frozenSideOf}
                              offsets={offsets}
                              {...shared}
                            />
                          ))}
                        </Fragment>
                      ))}
                    </TBody>
                  ))
                )}
              </Table>
            </TableWrap>
          )}
        </PanelBody>
      </Panel>

      {/* ── Below `md` — the same rows, stacked ─────────────────────────── */}
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-3 md:hidden">
        <FilterSheet
          keys={visibleKeys}
          headerFor={filters.headerFor}
          activeCount={filters.activeCount}
          clearAll={filters.clearAll}
        />
        {board.lanes.length === 0 ? (
          <Panel className="min-w-0">
            <PanelBody>
              <p className="m-0 py-4 text-center text-xs text-fg-subtle">
                No deals on any project in this cycle yet.
              </p>
            </PanelBody>
          </Panel>
        ) : bands.length === 0 ? (
          <Panel className="min-w-0">
            <PanelBody>
              <p className="m-0 py-4 text-center text-xs text-fg-subtle">
                No deals match the filters on this table.
              </p>
            </PanelBody>
          </Panel>
        ) : (
          bands.map((band) => (
            <section key={band.key} className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-3">
              <div className="flex min-w-0 items-center justify-between gap-2 rounded-md bg-surface-sunken px-3 py-2">
                <h2 className="m-0 min-w-0 text-xs font-semibold text-fg">
                  <BandHeading band={band} />
                </h2>
                {newDealButtonFor(band)}
              </div>
              {band.rows.length === 0 && (
                <p className="m-0 px-3 text-xs text-fg-subtle">{EMPTY_BAND_SENTENCE}</p>
              )}
              {runsOf(band, grouped).map((run) => (
                <Fragment key={run.key}>
                  {run.label !== null && (
                    <h3 className="m-0 px-3 text-2xs font-medium text-fg-muted">
                      <GroupHeading label={run.label} count={run.rows.length} />
                    </h3>
                  )}
                  {run.rows.map((row) => (
                    <StackedRow
                      key={row.deal.key}
                      deal={row.deal}
                      isVisible={isVisible}
                      {...shared}
                    />
                  ))}
                </Fragment>
              ))}
            </section>
          ))
        )}
      </div>

      {/* ⛔ ONE drawer for the whole table, OUTSIDE both layouts. */}
      <ActivityDrawer
        open={notesFor !== null}
        onClose={closeNotes}
        onActivityChanged={() => {
          notesChangedRef.current = true;
        }}
        target={{ type: 'company', id: notesFor?.id ?? 0 }}
        recordLabel={notesFor?.name.trim() || `Company #${notesFor?.id ?? ''}`}
        focus="notes"
        viewerId={user?.id ?? null}
        viewerTimeZone={viewerZone(user?.timezone)}
      />
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Cells
 * ------------------------------------------------------------------ */

/** Everything a cell needs that is the same for every row. */
interface CellSharedProps {
  /** The whole stage master, retired rows included. */
  stages: SalesTrackerDealStage[];
  stageIndex: ReadonlyMap<number, SalesTrackerDealStage>;
  /** The deal whose stage write is in flight — every copy of it disables. */
  pendingDealId: number | null;
  /** Absent = the stage is read-only. */
  onUpdateStage?: (row: SalesTrackerDealRow, stageId: number) => Promise<void>;
  onOpenNotes: (company: { id: number; name: string }) => void;
}

function RowCells({
  deal,
  visibleKeys,
  frozenSideOf,
  offsets,
  ...shared
}: CellSharedProps & {
  deal: SalesTrackerDealRow;
  visibleKeys: readonly string[];
  frozenSideOf: (key: string) => 'left' | 'right' | undefined;
  offsets: Record<string, number>;
}) {
  return (
    <TR aria-busy={shared.pendingDealId === deal.dealId || undefined}>
      {visibleKeys.map((key) => {
        const side = frozenSideOf(key);
        const frozenOffset = side === undefined ? undefined : (offsets[key] ?? 0);
        const content = <Cell columnKey={key} deal={deal} {...shared} />;

        // The deal heads its row. `TH` bakes in no vertical-align, so `align-top`
        // through `className` races nothing.
        if (key === ROW_HEADER_KEY) {
          return (
            <TH
              key={key}
              scope="row"
              className="align-top"
              frozen={side}
              frozenOffset={frozenOffset}
            >
              {content}
            </TH>
          );
        }

        return (
          <TD
            key={key}
            verticalAlign="top"
            numeric={key === 'units' || key === 'value'}
            frozen={side}
            frozenOffset={frozenOffset}
          >
            {content}
          </TD>
        );
      })}
    </TR>
  );
}

const DEAL_LINK_CLASS =
  'rounded-sm font-medium break-words text-fg underline-offset-2 decoration-hairline-strong transition-colors duration-150 ease-out hover:underline focus-visible:underline motion-reduce:transition-none';

function Cell({
  columnKey,
  deal,
  ...shared
}: CellSharedProps & { columnKey: string; deal: SalesTrackerDealRow }): ReactElement | null {
  switch (columnKey) {
    case 'deal':
      return (
        <DealNameLink
          dealId={deal.dealId}
          dealName={deal.dealName.trim() || `Deal #${deal.dealId}`}
          className={DEAL_LINK_CLASS}
        />
      );
    case 'company':
      return <CompanyCell deal={deal} />;
    case 'type': {
      const typeName = companyTypeName(deal);
      return typeName === null ? (
        <span className="text-fg-subtle">&mdash;</span>
      ) : (
        <span className="break-words">{typeName}</span>
      );
    }
    case 'stage':
      return <StageCell deal={deal} {...shared} />;
    case 'quoteSigned': {
      const signed = deal.quoteSigned;
      if (signed === undefined || signed === null) {
        return <span className="text-fg-subtle">&mdash;</span>;
      }
      return (
        <TextLink to={`/quotes/${signed.quoteId}`} className="whitespace-nowrap">
          {quoteSignedLabel(signed)}
        </TextLink>
      );
    }
    case 'units':
      // `null` = no counted line: a dash, never `0`.
      return deal.units === null ? (
        <span className="text-fg-subtle">&mdash;</span>
      ) : (
        <span className="tabular">{deal.units}</span>
      );
    case 'cities':
      return deal.cities.length === 0 ? (
        <span className="text-fg-subtle">&mdash;</span>
      ) : (
        <ValuePills
          items={deal.cities.map((city) => ({
            key: city.id,
            label: city.name.trim() || `#${city.id}`,
          }))}
        />
      );
    case 'value':
      return <Money amount={deal.amount} currency={deal.currency} />;
    case 'closingDate':
      return <span className="whitespace-nowrap">{formatDateOnly(deal.closingDate)}</span>;
    case 'accountLead':
      return <AccountLeadCell deal={deal} />;
    case 'lastNote':
    case 'lastNoteAt': {
      const company = deal.company;
      if (company === null) return <span className="text-fg-subtle">&mdash;</span>;
      return (
        <OpenNotesButton companyName={company.name} onOpen={() => shared.onOpenNotes(company)}>
          {columnKey === 'lastNote' ? (
            <AccountLastNoteCell note={company.accountLastNote} />
          ) : (
            <AccountLastNoteDateCell note={company.accountLastNote} />
          )}
        </OpenNotesButton>
      );
    }
    default:
      return null;
  }
}

/** ⛔ `shortName` is a SECOND LINE, never a substitute — two schools can share one. */
function CompanyCell({ deal }: { deal: SalesTrackerDealRow }) {
  const company = deal.company;
  if (company === null) return <span className="text-fg-subtle">&mdash;</span>;
  const shortName = company.shortName?.trim() ?? '';
  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="break-words text-fg">{company.name.trim() || `Company #${company.id}`}</span>
      {shortName !== '' && (
        <span className="text-xs text-fg-muted">
          <span className="sr-only">Short name: </span>
          {shortName}
        </span>
      )}
    </span>
  );
}

/** The sentence a refused stage write shows, ordered by specificity. */
function refusalOf(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.status === 403) return 'Your account cannot change this deal.';
    return cause.fieldErrors.stageId ?? cause.formMessage ?? cause.message;
  }
  const message = cause instanceof Error ? cause.message.trim() : '';
  return message === '' ? 'That didn’t save. Try again.' : message;
}

/**
 * The deal's STAGE — editable in place for a reader who may update deals.
 *
 * Options are the ACTIVE stages, plus the deal's own stored stage injected back
 * when it is retired — otherwise a native `<select>` whose value matches no
 * option renders BLANK. The write is `{ stageId }` and nothing else, and a
 * re-selection of the current stage sends nothing.
 */
function StageCell({
  deal,
  stages,
  stageIndex,
  pendingDealId,
  onUpdateStage,
}: CellSharedProps & { deal: SalesTrackerDealRow }) {
  const [error, setError] = useState<string | null>(null);
  const master = deal.stage === null ? undefined : stageIndex.get(deal.stage.id);
  const tone = master?.isWon ? 'success' : master?.isLost ? 'danger' : 'neutral';

  if (onUpdateStage === undefined) {
    if (deal.stage === null) return <span className="text-xs text-fg-subtle">No stage</span>;
    return (
      <Badge tone={tone}>
        <span className="sr-only">Deal status: </span>
        {deal.stage.name}
      </Badge>
    );
  }

  const active = stages.filter((stage) => stage.isActive);
  const storedOffered = deal.stage === null || active.some((stage) => stage.id === deal.stage?.id);
  const pending = pendingDealId === deal.dealId;

  return (
    <span className="block">
      <Select
        value={deal.stage === null ? '' : String(deal.stage.id)}
        disabled={pending}
        aria-label={`Deal status for ${deal.dealName}`}
        onChange={(event) => {
          const next = Number(event.target.value);
          if (!Number.isSafeInteger(next) || next <= 0) return;
          if (deal.stage !== null && next === deal.stage.id) return;
          setError(null);
          onUpdateStage(deal, next).catch((cause: unknown) => setError(refusalOf(cause)));
        }}
      >
        {deal.stage === null && <option value="">No stage</option>}
        {!storedOffered && deal.stage !== null && (
          <option value={String(deal.stage.id)}>{`${deal.stage.name} (retired)`}</option>
        )}
        {active.map((stage) => (
          <option key={stage.id} value={String(stage.id)}>
            {stage.name}
          </option>
        ))}
      </Select>
      {error !== null && (
        <span role="alert" className="mt-1 block text-2xs break-words text-danger">
          {error}
        </span>
      )}
    </span>
  );
}

/** Read-only: the account lead is the company's or the deal's field, changed on its own record. */
function AccountLeadCell({ deal }: { deal: SalesTrackerDealRow }) {
  const lead = deal.accountLead;
  if (lead === null) return null;
  const label = accountLeadLabel(deal);
  const hasName = lead.name.trim() !== '';
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5 text-xs">
      {hasName && <Avatar name={label} size="sm" />}
      <span className="break-words">{label}</span>
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Below `md`
 * ------------------------------------------------------------------ */

/** The fields a card lists, under the deal name — the Deal column is the title. */
const STACKED_FIELDS: readonly string[] = [
  'company',
  'type',
  'stage',
  'quoteSigned',
  'units',
  'cities',
  'value',
  'closingDate',
  'accountLead',
  'lastNote',
  'lastNoteAt',
];

function StackedRow({
  deal,
  isVisible,
  ...shared
}: CellSharedProps & { deal: SalesTrackerDealRow; isVisible: (key: string) => boolean }) {
  const shown = STACKED_FIELDS.filter(isVisible);
  return (
    <Panel className="min-w-0">
      <PanelHeader
        title={
          // `PanelHeader`'s heading truncates; below `md` the deal name is the
          // only thing identifying the row, so it wraps instead.
          <span className="block break-words whitespace-normal">
            <Cell columnKey="deal" deal={deal} {...shared} />
          </span>
        }
      />
      <PanelBody density="flush">
        {shown.length > 0 && (
          <dl className="m-0 grid grid-cols-[minmax(0,7rem)_minmax(0,1fr)] gap-x-3 gap-y-2 px-4 py-3 text-xs">
            {shown.map((key) => (
              <FieldRow
                key={key}
                label={labelOfSalesTrackerColumn(key)}
                value={<Cell columnKey={key} deal={deal} {...shared} />}
              />
            ))}
          </dl>
        )}
      </PanelBody>
    </Panel>
  );
}

function FieldRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <>
      <dt className="m-0 text-fg-muted">{label}</dt>
      <dd className="m-0 min-w-0 text-fg-secondary">{value}</dd>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Loading
 * ------------------------------------------------------------------ */

const SKELETON_ROWS = 6;

/** Rows in the real shape rather than a spinner. */
export function SalesTrackerTableSkeleton() {
  return (
    <div className="space-y-2 p-4">
      <Skeleton className="h-8 w-full" />
      {Array.from({ length: SKELETON_ROWS }, (_, row) => (
        <Skeleton key={row} className="h-10 w-full" />
      ))}
    </div>
  );
}
